'use client'

import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { mutate as swrMutate } from 'swr'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Circle,
  Loader2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { useAuthContext } from '@/hooks/use-auth-context'
import { useDataReadiness } from '@/hooks/use-data-readiness'
import { useBrandSyncReadiness } from '@/hooks/use-brand-sync-readiness'
import { parseImportCompleteFrame } from '@/hooks/use-credential-confirm-stream'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'
import { buildBrandUrl } from '@/lib/routes'
import { settlePostImportBinding } from '@/lib/post-import-binding-settlement'
import { AD_ACCOUNT_LIMIT_REACHED_ERROR_CODE } from '@/lib/import-error-codes'
import { aggregateWindowStatus } from '@/lib/data-readiness'
import { isSyncWidgetRoute } from '@/lib/sync-widget-route'
import {
  getLibraryChatOpenServerSnapshot,
  getLibraryChatShownSnapshot,
  LIBRARY_CHAT_PANEL_CLEARANCE_CLASS,
  subscribeLibraryChatOpen,
} from '@/lib/library-chat-panel-state'
import { cn } from '@/lib/utils'
import {
  rowVisualState,
  syncPillState,
  type AccountRowModel,
} from '@/lib/sync-readiness-rows'
import { useContextResourcesStore } from '@/stores/context-resources-store'
import { useBillingDialogStore } from '@/stores/billing-dialog-store'
import {
  dataReadinessKey,
  useSyncWidgetStore,
  type SyncImportSession,
} from '@/stores/sync-widget-store'
import { useCanSelfServeUpgrade } from '@/hooks/use-partner-billing'
import { SyncReadinessIssue } from './sync-readiness-issue'

/**
 * Bottom-right resident sync widget (WS2b).
 *
 * L2 truth: the readiness manifest polled by `useDataReadiness` — the widget
 * reconstructs itself from it on any page, any visit. L1 enhancement: live
 * import sessions in `sync-widget-store`, fed by the confirm-stream bridge.
 * Also the execution point for post-import brand binding when the user left
 * the oauth-callback page before the import finished (single-brand orgs).
 */

const SUCCESS_LINGER_MS = 5_000

type MilestoneStatus = 'done' | 'active' | 'pending' | 'failed' | 'stalled'

function milestoneFromReadiness(
  status: ReturnType<typeof aggregateWindowStatus>,
): MilestoneStatus {
  switch (status) {
    case 'complete':
      return 'done'
    case 'syncing':
      return 'active'
    case 'attention':
      return 'stalled'
  }
}

function MilestoneIcon({ status }: { status: MilestoneStatus }) {
  switch (status) {
    case 'done':
      return <Check className="size-3.5 text-emerald-600 dark:text-emerald-400" />
    case 'active':
      return <Loader2 className="size-3.5 animate-spin text-amber-500" />
    case 'failed':
    case 'stalled':
      return <AlertTriangle className="size-3.5 text-red-500" />
    default:
      return <Circle className="text-muted-foreground/40 size-3.5" />
  }
}

function AccountRow({
  row,
  orgSlug,
  brandSlug,
  canManageRecovery,
  retrying,
  retryAccepted,
  onRetry,
}: {
  row: AccountRowModel
  orgSlug: string
  brandSlug: string
  canManageRecovery: boolean
  retrying: boolean
  retryAccepted: boolean
  onRetry: (row: AccountRowModel) => void
}) {
  const t = useTranslations('Common.syncWidget')

  const created: MilestoneStatus = row.readiness
    ? 'done'
    : row.sessionStatus === 'done'
      ? 'done'
      : row.sessionStatus === 'error'
        ? 'failed'
        : 'active'
  // Same predicate the pill counts with, so a row that renders as "needs
  // attention" is always one of the accounts the count refers to.
  const hasIssue = rowVisualState(row) === 'attention'
  // A blocking issue (e.g. a disconnected account needing a reconnect) must not
  // read as "still syncing" — surface the recent-metrics milestone as attention
  // with the issue CTA below instead of an endless spinner.
  const recent: MilestoneStatus = hasIssue
    ? 'stalled'
    : row.readiness?.recent_data_state === 'mismatch'
      ? 'stalled'
      : row.readiness?.recent_data_state === 'unknown'
        ? 'active'
        : row.readiness
          ? milestoneFromReadiness(
              aggregateWindowStatus(row.readiness, 'last_30d'),
            )
          : 'pending'
  return (
    <div className="border-border/60 space-y-1 rounded-md border p-2">
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 text-xs font-medium">
          <span className="block truncate">{row.name}</span>
          {row.secondaryName && (
            <span className="text-muted-foreground block truncate text-[10px] font-normal">
              {row.secondaryName}
            </span>
          )}
        </span>
      </div>
      <ul className="space-y-0.5">
        <li className="flex items-center gap-1.5 text-xs">
          <MilestoneIcon status={created} />
          <span>
            {created === 'failed'
              ? t('milestoneCreateFailed')
              : t('milestoneCreated')}
          </span>
          {typeof row.campaignCount === 'number' && (
            <span className="text-muted-foreground">
              · {t('milestoneCampaigns', { count: row.campaignCount })}
            </span>
          )}
        </li>
        <li className="flex items-center gap-1.5 text-xs">
          <MilestoneIcon status={recent} />
          <span>{t('milestoneRecent')}</span>
        </li>
        {!hasIssue && row.readiness?.bootstrap_state === 'queued' && (
          <li className="text-muted-foreground flex items-center gap-1.5 text-xs">
            <MilestoneIcon status="pending" />
            <span>{t('milestoneHistoryQueued')}</span>
          </li>
        )}
      </ul>
      {hasIssue && (
        <SyncReadinessIssue
          row={row}
          orgSlug={orgSlug}
          brandSlug={brandSlug}
          canManageRecovery={canManageRecovery}
          retrying={retrying}
          retryAccepted={retryAccepted}
          onRetry={onRetry}
        />
      )}
      {row.bindingError && (
        <p className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          <span>{t('bindFailedNotice')}</span>
        </p>
      )}
    </div>
  )
}

export function SyncWidget() {
  const t = useTranslations('Common.syncWidget')
  const tImport = useTranslations('SettingsPages.oauthCallback')
  // A partner-managed workspace has no self-serve upgrade to offer, so the
  // CTA below points at the partner's support panel instead of Soku's plans.
  // The button stays: hiding it would leave a plan-limit error with no way out.
  const canSelfServeUpgrade = useCanSelfServeUpgrade()
  const tBillingCopy = useTranslations('Billing')
  const pathname = usePathname()
  const brand = useOptionalCurrentBrand()
  const { org } = useAuthContext()
  const canManageRecovery = org?.role === 'org:admin'
  const { data } = useDataReadiness()
  const allSessions = useSyncWidgetStore(state => state.sessions)
  const display = useSyncWidgetStore(state => state.display)
  const legacyModalOpen = useSyncWidgetStore(state => state.legacyModalOpen)
  const setDisplay = useSyncWidgetStore(state => state.setDisplay)
  const markBindingApplied = useSyncWidgetStore(
    state => state.markBindingApplied,
  )
  const markIntegrationCacheSettled = useSyncWidgetStore(
    state => state.markIntegrationCacheSettled,
  )
  const openBillingDialog = useBillingDialogStore(state => state.open)
  const libraryChatShown = useSyncExternalStore(
    subscribeLibraryChatOpen,
    getLibraryChatShownSnapshot,
    getLibraryChatOpenServerSnapshot,
  )

  // Brand switching is a client-side router.push and the store survives it:
  // render only the ACTIVE brand's sessions (the binding effect below stays
  // global on purpose — a finished import must bind even after a switch).
  const sessions = useMemo(() => {
    if (!brand) return {}
    const scoped: Record<number, SyncImportSession> = {}
    for (const [key, session] of Object.entries(allSessions)) {
      if (session.brandId === brand.brandId && session.visible !== false) {
        scoped[Number(key)] = session
      }
    }
    return scoped
  }, [allSessions, brand])

  const readinessAccounts = data?.accounts ?? []
  const hasPlanLimitError = Object.values(sessions).some(
    session =>
      session.errorCode === AD_ACCOUNT_LIMIT_REACHED_ERROR_CODE &&
      session.autoBind !== false,
  )
  // Shared with the Dashboard header so the two can never report different
  // counts. It also owns the "is anything actually happening" question: the
  // widget used to treat `recent_data_state === 'unknown'` as activity, which
  // made it appear with nothing to report and render "Syncing 0 account(s)…".
  const brandSync = useBrandSyncReadiness()
  const active = brandSync.hasWorkInFlight

  // Post-import binding: runs once per completed session (D14). This is the
  // navigation-safe home for the side effect that used to live in the
  // oauth-callback modal-close handler.
  const bindingInFlight = useRef<Set<number>>(new Set())
  useEffect(() => {
    for (const [key, session] of Object.entries(allSessions)) {
      const credentialId = Number(key)
      if (
        session.status !== 'reconciling' ||
        session.result === undefined ||
        session.autoBind === false ||
        session.bindingApplied ||
        bindingInFlight.current.has(credentialId)
      ) {
        continue
      }
      bindingInFlight.current.add(credentialId)
      const run = async () => {
        try {
          const result = parseImportCompleteFrame(session.result)
          const outcome = await settlePostImportBinding({
            credentialId,
            orgId: session.orgId,
            brandId: session.brandId,
            result,
            onOutcome: bindingOutcome =>
              markBindingApplied(
                credentialId,
                bindingOutcome.status === 'failed'
                  ? bindingOutcome.error
                  : undefined,
              ),
            onCacheSettled: () => markIntegrationCacheSettled(credentialId),
          })
          if (outcome.status === 'bound') {
            toast.success(t('boundToast'), {
              description: t('boundToastDescription', {
                count: outcome.accountCount,
              }),
            })
            void swrMutate(dataReadinessKey(session.brandId))
            // Composer/@-mention resources cache (5min TTL) must not keep
            // serving the pre-import list. Mirror the modal path when the
            // session's brand is the one being viewed; otherwise just bust
            // the cache so the next consumer refetches.
            const contextStore = useContextResourcesStore.getState()
            if (
              result.contextResources.length > 0 &&
              brand &&
              session.brandId === brand.brandId
            ) {
              contextStore.setResources(
                result.contextResources as never[],
                session.brandId,
              )
            } else {
              contextStore.reset()
            }
          } else if (outcome.status === 'failed') {
            console.error('[sync-widget] Binding failed', outcome.error)
            toast.error(t('bindFailedToast'))
          }
        } finally {
          bindingInFlight.current.delete(credentialId)
        }
      }
      void run()
    }
  }, [allSessions, markBindingApplied, markIntegrationCacheSettled, t, brand])

  // Success lingering: when the last active work item disappears, keep a
  // success card for a few seconds instead of vanishing mid-glance.
  const [showSuccess, setShowSuccess] = useState(false)
  const wasActive = useRef(false)
  useEffect(() => {
    if (active) {
      wasActive.current = true
      setShowSuccess(false)
      return
    }
    if (!wasActive.current) return
    wasActive.current = false
    setShowSuccess(true)
    const timer = setTimeout(() => setShowSuccess(false), SUCCESS_LINGER_MS)
    return () => clearTimeout(timer)
  }, [active])

  // Outside a brand layout there is no readiness scope to render.
  if (!brand) return null
  if (!isSyncWidgetRoute(pathname)) return null
  if (!active && !showSuccess) return null

  const effectiveDisplay = legacyModalOpen ? 'dot' : display
  const { rows, syncingCount, hasPendingSessions, importingCount } = brandSync
  const pill = syncPillState({
    hasPendingSessions,
    importingCount,
    syncingCount,
  })
  // `idle` renders no label at all, so the pill can never claim work that is
  // not there. `active` is itself derived from this same call (see
  // `hasWorkInFlight`), so reaching here with `idle` means the widget is only
  // on screen for the post-sync success linger, where `allDone` is the point.
  const pillLabel =
    pill.kind === 'importing'
      ? t('pillImporting', { count: pill.count })
      : pill.kind === 'syncing'
        ? t('pillSyncing', { count: pill.count })
        : null
  const recentReady = readinessAccounts.some(
    account => aggregateWindowStatus(account, 'last_30d') === 'complete',
  )
  const retryAccount = async (row: AccountRowModel) => {
    if (!row.readiness) return
    try {
      const result = await brandSync.retryAccount(row.readiness)
      toast.success(
        t(result.status === 'cooldown' ? 'retryAlreadyQueued' : 'retryQueued'),
      )
    } catch {
      toast.error(t('retryFailed'))
    }
  }

  if (effectiveDisplay === 'hidden') return null

  // On lg+ the Library side chat panel (400px, inline-end edge) would sit
  // under the widget; step clear of it while it is shown.
  const anchorClassName = cn(
    'fixed end-4 bottom-16 z-[100] max-md:hidden',
    libraryChatShown && LIBRARY_CHAT_PANEL_CLEARANCE_CLASS,
  )

  if (effectiveDisplay === 'dot') {
    return (
      <div className={anchorClassName}>
        <Button
          size="icon"
          variant="secondary"
          className="size-8 rounded-full shadow-md"
          aria-label={t('panelTitle')}
          onClick={() => setDisplay('collapsed')}
        >
          {pillLabel ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4 text-emerald-600 dark:text-emerald-400" />
          )}
        </Button>
      </div>
    )
  }

  return (
    <div className={anchorClassName}>
      {effectiveDisplay === 'collapsed' ? (
        <Button
          size="sm"
          variant="secondary"
          className="rounded-full shadow-md"
          onClick={() => setDisplay('expanded')}
        >
          {pillLabel ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Check className="size-4 text-emerald-600 dark:text-emerald-400" />
          )}
          {pillLabel ?? t('allDone')}
          <ChevronUp className="size-3.5" />
        </Button>
      ) : (
        <div className="bg-popover border-border w-80 space-y-2 rounded-lg border p-3 shadow-lg">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{t('panelTitle')}</p>
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                className="size-6"
                aria-label={t('collapse')}
                onClick={() => setDisplay('collapsed')}
              >
                <ChevronDown className="size-3.5" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="size-6"
                aria-label={t('hide')}
                onClick={() => setDisplay('dot')}
              >
                <X className="size-3.5" />
              </Button>
            </div>
          </div>
          {showSuccess && !active ? (
            <p className="text-xs text-emerald-600 dark:text-emerald-400">{t('allDone')}</p>
          ) : (
            <>
              {hasPlanLimitError && canManageRecovery && (
                <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2">
                  <p className="text-xs">
                    {tImport('importErrors.adAccountLimitReached')}
                  </p>
                  <Button
                    size="xs"
                    onClick={() => openBillingDialog('upgrade')}
                  >
                    {canSelfServeUpgrade
                      ? tImport('ctaUpgrade')
                      : tBillingCopy('partnerManagedShort')}
                  </Button>
                </div>
              )}
              <div className="max-h-72 space-y-2 overflow-y-auto">
                {rows.map(row => (
                  <AccountRow
                    key={row.key}
                    row={row}
                    orgSlug={brand.orgSlug}
                    brandSlug={brand.brandSlug}
                    canManageRecovery={canManageRecovery}
                    retrying={brandSync.retryingAccountIds.has(
                      row.readiness?.id ?? -1,
                    )}
                    retryAccepted={brandSync.acceptedRetryAccountIds.has(
                      row.readiness?.id ?? -1,
                    )}
                    onRetry={accountRow => void retryAccount(accountRow)}
                  />
                ))}
              </div>
            </>
          )}
          {recentReady && (
            <Button asChild size="sm" variant="outline" className="w-full">
              <Link
                href={`${buildBrandUrl(brand.orgSlug, brand.brandSlug)}/dashboards`}
              >
                {t('viewDashboard')}
              </Link>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
